/**
 * Email Account Configure Modal
 * Modal for configuring email account settings
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useListData } from "react-stately";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import { Button } from "@/components/base/buttons/button";
import { ModalOverlay, Modal, Dialog } from "@/components/application/modals/modal";
import { MultiSelectBase } from "@/components/base/select/multi-select";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import type { SelectItemType } from "@/components/base/select/select";
import { X, Save01, InfoCircle, RefreshCw01 } from "@untitledui/icons";
import type { EmailAccountResponse } from "@/types/api/email";
import { useEmailAccountFolders } from "@/hooks/api/use-email-accounts";

interface EmailAccountConfigureModalProps {
    account: EmailAccountResponse;
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: {
        display_name?: string;
        auto_process_attachments?: boolean;
        allowed_sender_domains?: string[];
        folders_to_watch?: string[];
        sync_from_date?: string | null;
    }) => void;
    onSync?: (account: EmailAccountResponse) => void;
    isLoading?: boolean;
}

// Helper function to determine initial sync option from date
const getInitialSyncOption = (syncFromDate: string | null): string => {
    if (!syncFromDate) return "none";
    const date = new Date(syncFromDate);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "today";
    if (diffDays <= 7) return "7days";
    if (diffDays <= 30) return "30days";
    return "30days"; // fallback
};

// Inner component that handles the MultiSelect with useListData
const ConfigureModalContent = ({
    account,
    availableFolders,
    isLoadingFolders,
    onClose,
    onSave,
    onSync,
    isLoading,
}: {
    account: EmailAccountResponse;
    availableFolders: { name: string; display_name: string; unread_count: number }[] | undefined;
    isLoadingFolders: boolean;
    onClose: () => void;
    onSave: EmailAccountConfigureModalProps["onSave"];
    onSync?: EmailAccountConfigureModalProps["onSync"];
    isLoading: boolean;
}) => {
    const { t } = useTranslation(["pages:email-settings"]);

    const [displayName, setDisplayName] = useState(account.display_name || "");
    const [autoProcess, setAutoProcess] = useState(account.auto_process_attachments ?? true);
    const [allowedDomains, setAllowedDomains] = useState(
        account.allowed_sender_domains?.join(", ") || ""
    );
    const [syncOption, setSyncOption] = useState<string>(() => {
        return getInitialSyncOption(account.sync_from_date);
    });
    const [showSyncConfirmModal, setShowSyncConfirmModal] = useState(false);

    // Convert folders to SelectItemType format
    // Use folder.name as id, but ensure uniqueness by using index as fallback
    const folderOptions: SelectItemType[] = useMemo(() => {
        if (!availableFolders) return [];
        // Use a Map to deduplicate folders by name (keep first occurrence)
        const seen = new Map<string, { name: string; display_name: string; unread_count: number }>();
        for (const folder of availableFolders) {
            if (!seen.has(folder.name)) {
                seen.set(folder.name, folder);
            }
        }
        return Array.from(seen.values()).map((folder) => ({
            id: folder.name,
            label: folder.display_name,
            supportingText: folder.unread_count > 0 ? `${folder.unread_count} unread` : undefined,
        }));
    }, [availableFolders]);

    // Build initial items for selected folders - only use folders_to_watch if they exist
    // Don't pre-select any folders if folders_to_watch is empty/null
    const initialSelectedItems = useMemo(() => {
        // Only use folders_to_watch if it exists and has items, otherwise start with empty array
        if (!account.folders_to_watch || account.folders_to_watch.length === 0) {
            return [];
        }

        // Only process if availableFolders are loaded
        if (!availableFolders || availableFolders.length === 0) {
            return [];
        }

        // Normalize folders: convert "INBOX" to "Inbox" for non-Gmail providers
        const normalizedFolders = account.folders_to_watch.map((folderName) => {
            if (account.provider !== "gmail" && folderName === "INBOX") {
                return "Inbox";
            }
            return folderName;
        });

        // Only include folders that exist in availableFolders
        const items: SelectItemType[] = [];
        for (const folderName of normalizedFolders) {
            const folderInfo = availableFolders.find((f) => f.name === folderName);
            if (folderInfo) {
                items.push({
                    id: folderName,
                    label: folderInfo.display_name,
                });
            }
        }
        return items;
    }, [account.folders_to_watch, availableFolders, account.provider]);

    // Initialize selected folders - useListData is only called once per mount
    const selectedFolders = useListData<SelectItemType>({
        initialItems: initialSelectedItems,
    });

    // Update selectedFolders only once when availableFolders first loads
    // This ensures folders_to_watch are properly displayed, but doesn't interfere with user selections
    const hasInitializedFoldersRef = useRef(false);
    useEffect(() => {
        // Only run once when availableFolders first loads and we haven't initialized yet
        if (!availableFolders || availableFolders.length === 0 || hasInitializedFoldersRef.current) {
            return;
        }

        // If we have folders_to_watch, update the selected items with proper display names
        if (account.folders_to_watch && account.folders_to_watch.length > 0) {
            const normalizedFolders = account.folders_to_watch.map((folderName) => {
                if (account.provider !== "gmail" && folderName === "INBOX") {
                    return "Inbox";
                }
                return folderName;
            });

            const updatedItems: SelectItemType[] = [];
            const seenIds = new Set<string>(); // Track IDs to avoid duplicates
            for (const folderName of normalizedFolders) {
                const folderInfo = availableFolders.find((f) => f.name === folderName);
                if (folderInfo && !seenIds.has(folderName)) {
                    seenIds.add(folderName);
                    updatedItems.push({
                        id: folderName,
                        label: folderInfo.display_name,
                    });
                }
            }

            // Only add items that aren't already selected (don't remove user selections)
            const currentKeys = new Set(selectedFolders.items.map(item => item.id));
            for (const item of updatedItems) {
                if (!currentKeys.has(item.id)) {
                    selectedFolders.append(item);
                }
            }
        }

        // Mark as initialized so we don't run again
        hasInitializedFoldersRef.current = true;
    }, [availableFolders, account.folders_to_watch, account.provider]);

    // Sync date options for the Select component
    const syncDateOptions: SelectItemType[] = useMemo(() => [
        { id: "none", label: t("configure.syncFromNone") },
        { id: "today", label: t("configure.syncFromToday") },
        { id: "7days", label: t("configure.syncFrom7days") },
        { id: "30days", label: t("configure.syncFrom30days") },
    ], [t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const domainsArray = allowedDomains
            .split(",")
            .map((d) => d.trim())
            .filter((d) => d.length > 0);

        // Normalize folder names: convert "INBOX" to "Inbox" for non-Gmail providers
        const foldersArray = selectedFolders.items.map((item) => {
            const folderName = item.id as string;
            if (account.provider !== "gmail" && folderName === "INBOX") {
                return "Inbox";
            }
            return folderName;
        });

        // Compute sync_from_date based on selected option
        // "none" = new emails only → set to now() so null exclusively means "not configured"
        let syncFromDate: string | null = null;
        if (syncOption === "none") {
            syncFromDate = new Date().toISOString();
        } else if (syncOption === "today") {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            syncFromDate = d.toISOString();
        } else if (syncOption === "7days") {
            const d = new Date();
            d.setDate(d.getDate() - 7);
            d.setHours(0, 0, 0, 0);
            syncFromDate = d.toISOString();
        } else if (syncOption === "30days") {
            const d = new Date();
            d.setDate(d.getDate() - 30);
            d.setHours(0, 0, 0, 0);
            syncFromDate = d.toISOString();
        }

        try {
            // Save configuration
            // Don't set default folder - let user explicitly select folders
            await onSave({
                display_name: displayName || undefined,
                auto_process_attachments: autoProcess,
                allowed_sender_domains: domainsArray.length > 0 ? domainsArray : undefined,
                folders_to_watch: foldersArray.length > 0 ? foldersArray : undefined,
                sync_from_date: syncFromDate,
            });

            // Show sync confirmation modal after successful save
            if (onSync) {
                setShowSyncConfirmModal(true);
            } else {
                // If no onSync handler, just close the modal
                onClose();
            }
        } catch (error) {
            // Error is already handled by onSave (toast shown in email-accounts-list)
            // Don't show sync modal on error
        }
    };

    const handleSyncConfirm = () => {
        setShowSyncConfirmModal(false);
        if (onSync) {
            onSync(account);
        }
        onClose();
    };

    const handleSyncCancel = () => {
        setShowSyncConfirmModal(false);
        onClose();
    };

    return (
        <div className="w-full rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-primary">
                    {t("configure.title")}
                </h2>
                <Button
                    onClick={onClose}
                    color="tertiary"
                    size="sm"
                    iconLeading={X}
                    aria-label="Close"
                />
            </div>

            {/* Disclaimer */}
            <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-tertiary p-3">
                <InfoCircle className="mt-0.5 h-4 w-4 shrink-0 text-quaternary" />
                <p className="text-xs leading-relaxed text-secondary">
                    {t("configure.disclaimer")}
                </p>
            </div>

            {/* Account Email Display */}
            <div className="mb-5 rounded-lg bg-secondary p-3">
                <span className="text-sm text-tertiary">
                    {account.email_address}
                </span>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Display Name */}
                <Input
                    label={t("emailEntry.displayNameLabel")}
                    value={displayName}
                    onChange={(value) => setDisplayName(value)}
                    placeholder={t("emailEntry.displayNamePlaceholder")}
                />

                {/* Auto Process Attachments */}
                <Toggle
                    label={t("configure.autoProcess")}
                    hint={t("configure.autoProcessHint")}
                    isSelected={autoProcess}
                    onChange={setAutoProcess}
                    size="md"
                />

                {/* Allowed Sender Domains */}
                <Input
                    label={t("configure.allowedDomains")}
                    value={allowedDomains}
                    onChange={(value) => setAllowedDomains(value)}
                    placeholder={t("configure.allowedDomainsPlaceholder")}
                    hint={t("configure.allowedDomainsHint")}
                />

                {/* Folders to Watch */}
                <MultiSelectBase
                    label={t("configure.foldersToWatch")}
                    placeholder={isLoadingFolders ? t("configure.loadingFolders") : t("configure.selectFolders")}
                    items={folderOptions}
                    selectedItems={selectedFolders}
                    size="md"
                    hint={t("configure.foldersToWatchHint")}
                    isDisabled={isLoadingFolders}
                >
                    {(item) => (
                        <SelectItem id={item.id}>
                            {item.label}
                            {item.supportingText && (
                                <span className="text-tertiary"> ({item.supportingText})</span>
                            )}
                        </SelectItem>
                    )}
                </MultiSelectBase>

                {/* Sync From Date */}
                <div className="space-y-1.5">
                    <Select
                        label={t("configure.syncFromDate")}
                        items={syncDateOptions}
                        selectedKey={syncOption}
                        onSelectionChange={(key) => setSyncOption(key as string)}
                        size="md"
                        hint={account.last_sync_at
                            ? t("configure.syncFromDateAlreadySynced")
                            : t("configure.syncFromDateHint")}
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id} label={item.label}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                    <Button
                        type="button"
                        onClick={onClose}
                        color="secondary"
                        size="sm"
                    >
                        {t("actions.cancel")}
                    </Button>
                    <Button
                        type="submit"
                        isDisabled={isLoading}
                        isLoading={isLoading}
                        color="primary"
                        size="sm"
                        iconLeading={!isLoading ? Save01 : undefined}
                    >
                        {isLoading ? t("actions.saving") : t("actions.save")}
                    </Button>
                </div>
            </form>

            {/* Sync Confirmation Modal */}
            {showSyncConfirmModal && (
                <ModalOverlay isOpen={showSyncConfirmModal} onOpenChange={(open) => !open && handleSyncCancel()}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="w-full rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                {/* Header */}
                                <div className="mb-6 flex items-center justify-between">
                                    <h2 className="text-lg font-semibold text-primary">
                                        {t("configure.syncConfirmTitle")}
                                    </h2>
                                    <Button
                                        onClick={handleSyncCancel}
                                        color="tertiary"
                                        size="sm"
                                        iconLeading={X}
                                        aria-label="Close"
                                    />
                                </div>

                                {/* Message */}
                                <p className="mb-6 text-sm text-tertiary">
                                    {t("configure.syncConfirmMessage")}
                                </p>

                                {/* Actions */}
                                <div className="flex justify-end gap-3">
                                    <Button
                                        onClick={handleSyncCancel}
                                        color="secondary"
                                        size="sm"
                                    >
                                        {t("actions.cancel")}
                                    </Button>
                                    <Button
                                        onClick={handleSyncConfirm}
                                        color="primary"
                                        size="sm"
                                        iconLeading={RefreshCw01}
                                    >
                                        {t("configure.syncNow")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

export const EmailAccountConfigureModal = ({
    account,
    isOpen,
    onClose,
    onSave,
    onSync,
    isLoading = false,
}: EmailAccountConfigureModalProps) => {
    // Fetch available folders for this account
    const { data: availableFolders, isLoading: isLoadingFolders } = useEmailAccountFolders(
        account.account_id,
        { enabled: isOpen }
    );

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal className="max-w-lg">
                <Dialog>
                    {/* Key forces remount when account changes, resetting useListData state */}
                    <ConfigureModalContent
                        key={account.account_id}
                        account={account}
                        availableFolders={availableFolders}
                        isLoadingFolders={isLoadingFolders}
                        onClose={onClose}
                        onSave={onSave}
                        onSync={onSync}
                        isLoading={isLoading}
                    />
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
